import { Checkbox, Icons } from '@commenda/kakashi';
import { cn } from '@commenda/utils';
import { useEffect, useRef, type ReactNode } from 'react';
import { QuestionBlockHelper } from './question-block';

/** The Figma "Question / Option row" family (node 1257:851), three states:
 * - Default: flush row, grey index badge, hairline divider below.
 * - Focused (hover or keyboard): the row's content lifts into a filled inset pill.
 * - Selected: the lead goes brand-filled; the row itself stays unfilled.
 *
 * Every row renders the inset structure permanently (p-xs frame around a p-md field, summing to
 * the design's 12px), so gaining the focus fill never shifts the layout. */

interface RowFrameProps {
  isDisabled: boolean;
  /** Last row in a list carries no divider. */
  hasDivider: boolean;
  children: ReactNode;
}

const frameClassName = ({
  isDisabled,
  hasDivider,
}: Omit<RowFrameProps, 'children'>) =>
  cn(
    'group flex w-full border-b-[0.5px] p-xs text-left',
    // The divider belongs to the resting row only: the focused state drops it (Figma), and the
    // last row never has one. Kept as a transparent border either way so nothing reflows.
    hasDivider ? 'border-border-primary' : 'border-transparent',
    hasDivider && !isDisabled && 'hover:border-transparent',
    isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
  );

/** Every field reserves a 1px transparent border, so the write-in row taking its brand border on
 * focus swaps a colour rather than adding width, and nothing jumps as focus moves in and out.
 * `isInteractive: false` opts out of the hover tint for the write-in field, which stays white. */
const fieldClassName = ({
  isDisabled,
  isInteractive = true,
}: {
  isDisabled: boolean;
  isInteractive?: boolean;
}) =>
  cn(
    'flex min-w-0 flex-1 items-start gap-lg rounded-md border border-transparent p-md transition-colors',
    isInteractive &&
      !isDisabled &&
      'group-hover:bg-bg-active-light group-focus-visible:bg-bg-active-light'
  );

/** Grey square carrying the option's number, or the write-in pencil. Three tones per Figma: grey
 * at rest, grey with a darker glyph while typing, brand-filled with a white glyph once chosen.
 * The tone lives on this element alone so the glyph's currentColor has one source. */
type RowLeadTone = 'rest' | 'typing' | 'selected';

const ROW_LEAD_TONE: Record<RowLeadTone, string> = {
  rest: 'bg-bg-tertiary text-text-tertiary',
  typing: 'bg-bg-tertiary text-text-tertiary-hover',
  selected: 'bg-fg-brand text-fg-white',
};

function RowLead({
  children,
  tone,
}: {
  children: ReactNode;
  tone: RowLeadTone;
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'flex size-6 shrink-0 items-center justify-center rounded-sm txt-extra-small-medium tabular-nums transition-colors',
        ROW_LEAD_TONE[tone]
      )}
    >
      {children}
    </span>
  );
}

function RowText({
  title,
  description,
  /** Option titles are medium; the write-in row's label is regular (Figma). */
  isTitleRegular = false,
  isMuted = false,
}: {
  title: string;
  description?: string;
  isTitleRegular?: boolean;
  isMuted?: boolean;
}) {
  return (
    <span className="flex min-w-0 flex-1 flex-col text-left">
      <span
        className={cn(
          isTitleRegular ? 'txt-small-regular' : 'txt-small-medium',
          isMuted ? 'text-text-tertiary' : 'text-text-primary'
        )}
      >
        {title}
      </span>
      {description && (
        <span className="txt-small-regular text-text-tertiary">
          {description}
        </span>
      )}
    </span>
  );
}

interface QuestionOptionRowProps {
  lead: { kind: 'number'; value: number } | { kind: 'checkbox' };
  title: string;
  description?: string;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: () => void;
}

export function QuestionOptionRow({
  lead,
  title,
  description,
  isSelected,
  isDisabled,
  onToggle,
}: QuestionOptionRowProps) {
  const field = (
    <span className={fieldClassName({ isDisabled })}>
      {lead.kind === 'number' ? (
        <RowLead tone={isSelected ? 'selected' : 'rest'}>{lead.value}</RowLead>
      ) : (
        <Checkbox
          checked={isSelected}
          disabled={isDisabled}
          onCheckedChange={onToggle}
          className="mt-xxs shrink-0"
        />
      )}
      <RowText title={title} description={description} />
    </span>
  );

  const frame = frameClassName({ isDisabled, hasDivider: true });

  if (lead.kind === 'checkbox') return <label className={frame}>{field}</label>;

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      disabled={isDisabled}
      onClick={onToggle}
      className={cn(frame, 'focus-visible:outline-none')}
    >
      {field}
    </button>
  );
}

interface QuestionWriteInRowProps {
  value: string;
  /** Typing state: the field takes a brand border. Resting: a row that activates on click. */
  isActive: boolean;
  isDisabled: boolean;
  onActivate: () => void;
  /** Blur leaves the typing state: empty goes back to the placeholder row, text to the
   * answered row. The brand border belongs to typing only. */
  onDeactivate: () => void;
  onChange: (value: string) => void;
  /** Enter in the field. The card decides whether that submits or advances the pager. */
  onSubmit: () => void;
}

/** Always the last row in a list (Figma "Write-in / usage", node 1476:917): resting, typing, and
 * answered. Carries no divider, since nothing follows it. */
export function QuestionWriteInRow({
  value,
  isActive,
  isDisabled,
  onActivate,
  onDeactivate,
  onChange,
  onSubmit,
}: QuestionWriteInRowProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const hasValue = value.trim().length > 0;

  useEffect(() => {
    if (isActive) inputRef.current?.focus();
  }, [isActive]);

  const lead = (
    <RowLead tone={isActive ? 'typing' : hasValue ? 'selected' : 'rest'}>
      <Icons.Pencil01 className="size-4" />
    </RowLead>
  );

  if (!isActive)
    return (
      <button
        type="button"
        disabled={isDisabled}
        onClick={onActivate}
        className={cn(
          frameClassName({ isDisabled, hasDivider: false }),
          'focus-visible:outline-none'
        )}
      >
        <span className={cn(fieldClassName({ isDisabled }), 'items-center')}>
          {lead}
          <RowText
            title={hasValue ? value.trim() : 'Write your own answer'}
            isTitleRegular
            isMuted={!hasValue}
          />
        </span>
      </button>
    );

  // Typing: one brand-bordered field enclosing the lead and the input together (Figma), never a
  // bare input box beside the lead.
  return (
    <label className={frameClassName({ isDisabled, hasDivider: false })}>
      <span
        className={cn(
          fieldClassName({ isDisabled, isInteractive: false }),
          'items-center border-fg-brand bg-bg-primary'
        )}
      >
        {lead}
        <input
          ref={inputRef}
          type="text"
          value={value}
          disabled={isDisabled}
          placeholder="Write your own answer"
          aria-label="Write your own answer"
          maxLength={QuestionBlockHelper.ANSWER_MAX_LENGTH}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onDeactivate}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && hasValue) {
              event.preventDefault();
              onSubmit();
            }
          }}
          className="h-6 w-full min-w-0 border-0 bg-transparent p-none txt-small-regular text-text-primary shadow-none outline-none focus:ring-0 placeholder:text-text-placeholder"
        />
      </span>
    </label>
  );
}
